import { useState, useEffect } from "react";
import { API } from '../../config/api';
import axios from "axios";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import {
  FileText, Download, Loader2, CheckCircle2, Clock, Link2, Landmark
} from "lucide-react";

// BM-H500 (Vanessa, via Jodie/Rebekah on Teams): fee earners filing an
// SDLT return had to hunt for the client's NI number / UTR-VAT / CoHo
// auth code elsewhere on the file even though onboarding already
// captured them. Pulled straight from the onboarding record (the same
// GET /onboarding/status/{ref} the ConflictsSearch and ThirdfortReviewPanel
// screens already use) so they're visible right where the SDLT link is
// generated and tracked, without a second data-entry step.
function TaxReferenceDetails({ matterRef }) {
  const [ob, setOb] = useState(null);

  useEffect(() => {
    if (!matterRef) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/onboarding/status/${encodeURIComponent(matterRef)}`);
        setOb(res.data);
      } catch { /* no linked onboarding record yet — not critical here */ }
    })();
  }, [matterRef]);

  if (!ob) return null;

  const primary = ob.primary_client || {};
  const isCorporate = ["company", "llp"].includes(ob.entity_type);
  const niNumber = primary.national_insurance_number || "";
  const utrOrVat = primary.utr_or_vat || "";
  const utrVatType = primary.utr_vat_type || "VAT";
  const cohoCode = primary.companies_house_auth_code || "";

  if (!niNumber && !utrOrVat && !cohoCode) return null;

  return (
    <div className="mt-4 p-4 bg-navy-hover/60 rounded-md border border-navy-border" data-testid="sdlt-tax-reference-details">
      <div className="flex items-center gap-2 mb-2">
        <Landmark className="w-4 h-4 text-teal" />
        <p className="text-xs text-slate-400 font-medium uppercase tracking-wider">Tax Reference Details (from Onboarding)</p>
      </div>
      <div className="space-y-1.5 text-sm">
        {niNumber && (
          <div className="flex justify-between gap-3" data-testid="sdlt-ni-number-row">
            <span className="text-txt-muted">National Insurance Number</span>
            <span className="text-white font-mono">{niNumber}</span>
          </div>
        )}
        {utrOrVat && (
          <div className="flex justify-between gap-3" data-testid="sdlt-utr-vat-row">
            <span className="text-txt-muted">{utrVatType} Number</span>
            <span className="text-white font-mono">{utrOrVat}</span>
          </div>
        )}
        {isCorporate && cohoCode && (
          <div className="flex justify-between gap-3" data-testid="sdlt-coho-auth-code-row">
            <span className="text-txt-muted">Companies House Auth Code</span>
            <span className="text-white font-mono">{cohoCode}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function SDLTLinkSection({ submissionId, data, matterRef }) {
  const [sdltStatus, setSdltStatus] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/sdlt/status/${submissionId}`);
        setSdltStatus(res.data);
      } catch { /* not critical */ }
    })();
  }, [submissionId]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/sdlt/generate-link`, {
        submission_id: submissionId,
        client_name: data.Q1 || "",
        matter_reference: data.Q4 || "",
        property_address: data.property_address || "",
        fee_earner: data.Q6 || "",
      });
      setSdltStatus({ exists: true, token: res.data.token, status: "pending", link: res.data.link });
      toast.success("SDLT questionnaire link created");
    } catch (err) {
      toast.error(errorMessage(err, "Failed to generate link"));
    } finally {
      setGenerating(false);
    }
  };

  const copyLink = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}${sdltStatus?.link}`;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Link copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/sdlt/pdf/${submissionId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `SDLT_Questionnaire.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF not available yet");
    }
  };

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-teal" />
        <h2 className="text-xl font-bold text-teal">SDLT / LTT Questionnaire</h2>
      </div>
      <div className="bg-white rounded-md border border-navy-border shadow-sm p-5" data-testid="sdlt-section">
        {!sdltStatus?.exists ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Generate a questionnaire link to send to the client for SDLT/LTT information.</p>
              <p className="text-xs text-txt-muted mt-1">The completed form will be saved as a PDF to the matter folder.</p>
            </div>
            <Button onClick={handleGenerate} disabled={generating}
              className="bg-teal hover:bg-teal-hover text-white gap-2" data-testid="sdlt-generate-btn">
              {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Generate Link
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {sdltStatus.status === "completed"
                ? <CheckCircle2 className="w-5 h-5 text-status-green" />
                : <Clock className="w-5 h-5 text-status-amber" />}
              <span className="text-sm font-medium text-white">
                {sdltStatus.status === "completed" ? "Questionnaire Completed" : "Awaiting Client Response"}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-xs bg-navy-hover text-txt-muted px-3 py-1.5 rounded flex-1 truncate">
                {process.env.REACT_APP_BACKEND_URL || ""}{sdltStatus.link}
              </code>
              <Button size="sm" variant="outline" onClick={copyLink} className="text-xs gap-1" data-testid="sdlt-copy-btn">
                {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-status-green" /> : <Link2 className="w-3.5 h-3.5" />}
                {copied ? "Copied" : "Copy Link"}
              </Button>
              {sdltStatus.status === "completed" && (
                <Button size="sm" variant="outline" onClick={handleDownloadPdf} className="text-xs gap-1" data-testid="sdlt-download-pdf-btn">
                  <Download className="w-3.5 h-3.5" /> PDF
                </Button>
              )}
            </div>
          </div>
        )}
        <TaxReferenceDetails matterRef={matterRef} />
      </div>
    </div>
  );
}
